import * as React from "react";
import { Camera, Loader2, Sparkles, X, Check, Plus, RefreshCcw, AlertTriangle } from "lucide-react";
import { apiFetch } from "@/lib/api.js";
import { useToast } from "@/components/Toast.js";
import { Modal } from "@/components/Modal.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { cn } from "@/lib/utils.js";

/**
 * ScanToItemsModal — the shared "photo → detected items → create-or-match" flow
 * (F-f). One primitive consumed by both Find-a-home "New scan" and the
 * Reorganize "Scan this space" action.
 *
 * Pipeline:
 *   1. Photo (upload/capture) → base64 → POST /api/ai/identify-auto (detect items)
 *   2. User confirms the detected list (edit names, drop false positives) — the
 *      confirm step is required; nothing is written before it.
 *   3. POST /api/items/resolve-scan (create=false) previews the create-or-match
 *      split (matched existing vs new), deduped by name within `targetSpaceId`.
 *   4. On confirm, POST /api/items/resolve-scan (create=true) creates the new
 *      items (placed in `targetSpaceId`) and returns the full resolved id set,
 *      which is handed back via `onResolved` for linking.
 */

interface DetectedItem {
  name: string;
  description: string | null;
  quantity: number;
  tags: string[];
}

interface ResolvePreview {
  matched: { name: string; itemId: string }[];
  toCreate: string[];
  resolvedItemIds: string[];
}

type Phase = "capture" | "detecting" | "confirm" | "resolving";

export function ScanToItemsModal({
  open,
  onClose,
  targetSpaceId,
  targetSpaceName,
  onResolved,
  title = "Scan for items",
}: {
  open: boolean;
  onClose: () => void;
  /** Space to dedupe within and place newly-created items into. */
  targetSpaceId?: string | null;
  targetSpaceName?: string | null;
  /** Called with the resolved item IDs (matched + created) after confirmation. */
  onResolved: (itemIds: string[]) => void | Promise<void>;
  title?: string;
}) {
  const toast = useToast();
  const { aiEnabled } = useFeatureFlags();
  const [phase, setPhase] = React.useState<Phase>("capture");
  const [detected, setDetected] = React.useState<DetectedItem[]>([]);
  const [preview, setPreview] = React.useState<ResolvePreview | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  const reset = React.useCallback(() => {
    setPhase("capture");
    setDetected([]);
    setPreview(null);
  }, []);

  const handleClose = () => { reset(); onClose(); };

  const handlePhoto = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = async () => {
      const base64 = (reader.result as string).split(",")[1];
      setPhase("detecting");
      try {
        const res = await apiFetch<
          | { mode: "single"; item: DetectedItem }
          | { mode: "multi"; items: DetectedItem[] }
        >("/api/ai/identify-auto", {
          method: "POST",
          body: JSON.stringify({ imageBase64: base64, mimeType: file.type }),
        });
        const items = res.mode === "single" ? [res.item] : res.items;
        if (items.length === 0) {
          toast.show("No items detected — try another photo.", "error");
          setPhase("capture");
          return;
        }
        setDetected(items.map((i) => ({
          name: i.name,
          description: i.description ?? null,
          quantity: i.quantity ?? 1,
          tags: i.tags ?? [],
        })));
        setPhase("confirm");
      } catch (err: any) {
        toast.show(err?.status === 429 ? "AI action limit reached" : "Couldn't analyze that photo. Try again.", "error");
        setPhase("capture");
      }
    };
    reader.readAsDataURL(file);
    // Allow re-selecting the same file later.
    e.target.value = "";
  };

  // Preview the create-or-match split whenever the confirmed list changes.
  React.useEffect(() => {
    if (phase !== "confirm" || detected.length === 0) { setPreview(null); return; }
    let cancelled = false;
    apiFetch<ResolvePreview>("/api/items/resolve-scan", {
      method: "POST",
      body: JSON.stringify({ detected, targetSpaceId: targetSpaceId ?? null, create: false }),
    })
      .then((p) => { if (!cancelled) setPreview(p); })
      .catch(() => { if (!cancelled) setPreview(null); });
    return () => { cancelled = true; };
  }, [phase, detected, targetSpaceId]);

  const updateName = (idx: number, name: string) => {
    setDetected((prev) => prev.map((d, i) => (i === idx ? { ...d, name } : d)));
  };
  const removeDetected = (idx: number) => {
    setDetected((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleConfirm = async () => {
    const cleaned = detected.map((d) => ({ ...d, name: d.name.trim() })).filter((d) => d.name.length > 0);
    if (cleaned.length === 0) { toast.show("Add at least one item.", "error"); return; }
    setPhase("resolving");
    try {
      const res = await apiFetch<ResolvePreview & { created: { id: string; name: string }[] }>(
        "/api/items/resolve-scan",
        {
          method: "POST",
          body: JSON.stringify({ detected: cleaned, targetSpaceId: targetSpaceId ?? null, create: true }),
        }
      );
      const createdCount = res.created?.length ?? 0;
      const matchedCount = res.matched?.length ?? 0;
      toast.show(
        `${createdCount} added${matchedCount > 0 ? `, ${matchedCount} matched existing` : ""}.`,
        "success"
      );
      await onResolved(res.resolvedItemIds);
      handleClose();
    } catch (err: any) {
      toast.show(err?.message || "Couldn't save those items. Try again.", "error");
      setPhase("confirm");
    }
  };

  const matchedNames = React.useMemo(
    () => new Set((preview?.matched ?? []).map((m) => m.name.trim().toLowerCase())),
    [preview]
  );

  return (
    <Modal open={open} onClose={handleClose} title={title}>
      <div className="space-y-4">
        {!aiEnabled ? (
          <div className="flex items-start gap-2.5 p-3 rounded-md bg-warning-wash border border-warning/30">
            <AlertTriangle className="w-4 h-4 text-warning shrink-0 mt-0.5" />
            <p className="text-[0.8125rem] text-warning-text">Photo scanning needs AI, which is turned off. You can still add items manually.</p>
          </div>
        ) : phase === "capture" ? (
          <div className="text-center py-4">
            <div className="w-14 h-14 rounded-md bg-sage-wash flex items-center justify-center mx-auto mb-3">
              <Camera className="w-7 h-7 text-sage-deep" />
            </div>
            <p className="text-sm text-body mb-1">Take or upload a photo{targetSpaceName ? ` of ${targetSpaceName}` : ""}.</p>
            <p className="text-[0.8125rem] text-faint mb-4">We'll detect the items so you can confirm and add them.</p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 bg-sage-deep text-white rounded-md text-sm font-medium hover:bg-sage-deep-hover transition-colors"
            >
              <Camera className="w-4 h-4" />Choose a photo
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" capture="environment" onChange={handlePhoto} className="hidden" />
          </div>
        ) : phase === "detecting" ? (
          <div className="flex flex-col items-center justify-center py-10">
            <Loader2 className="w-6 h-6 text-sage-deep animate-spin mb-2" />
            <p className="text-sm text-faint">Detecting items…</p>
          </div>
        ) : (
          <>
            <p className="text-[0.8125rem] text-faint">
              Confirm what to add{targetSpaceName ? ` to ${targetSpaceName}` : ""}. Items already there are matched, not duplicated.
            </p>
            <div className="max-h-64 overflow-y-auto space-y-1.5">
              {detected.map((d, idx) => {
                const isMatch = matchedNames.has(d.name.trim().toLowerCase());
                return (
                  <div key={idx} className="flex items-center gap-2 py-1.5 px-2 rounded-md hover:bg-amethyst-soft/50 transition-colors">
                    <input
                      value={d.name}
                      onChange={(e) => updateName(idx, e.target.value)}
                      className="flex-1 min-w-0 text-sm text-body px-2 py-1.5 border border-clay-border rounded-sm focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary"
                    />
                    <span
                      className={cn(
                        "text-[0.8125rem] px-2 py-0.5 rounded-pill font-medium shrink-0",
                        isMatch ? "bg-amethyst-soft text-amethyst-ink" : "bg-sage-wash text-sage-deep"
                      )}
                    >
                      {isMatch ? "matched" : "new"}
                    </span>
                    <button onClick={() => removeDetected(idx)} className="p-1.5 text-faint hover:text-error shrink-0 transition-colors" aria-label="Remove detected item">
                      <X className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
              {detected.length === 0 && (
                <p className="text-sm text-faint text-center py-4">Nothing to add — rescan or close.</p>
              )}
            </div>

            {preview && (
              <div className="flex items-center gap-3 px-3 py-2 rounded-md bg-paper-raised border border-clay-border text-[0.8125rem] text-faint">
                <span className="flex items-center gap-1 tabular-nums"><Plus className="w-3 h-3 text-sage-deep" />{preview.toCreate.length} new</span>
                <span className="flex items-center gap-1 tabular-nums"><Check className="w-3 h-3 text-primary" />{preview.matched.length} matched</span>
              </div>
            )}

            <div className="flex gap-2">
              <button
                onClick={reset}
                disabled={phase === "resolving"}
                className="py-2.5 px-3 bg-amethyst-soft text-amethyst-ink rounded-md text-sm font-medium hover:bg-amethyst-soft/70 transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center gap-1.5"
              >
                <RefreshCcw className="w-3.5 h-3.5" />Rescan
              </button>
              <button
                onClick={handleConfirm}
                disabled={phase === "resolving" || detected.length === 0}
                className="flex-1 py-2.5 bg-sage-deep text-white rounded-md text-sm font-medium hover:bg-sage-deep-hover transition-colors disabled:bg-clay-muted disabled:text-body disabled:cursor-not-allowed flex items-center justify-center gap-1.5"
              >
                {phase === "resolving" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                {phase === "resolving" ? "Adding…" : "Add these items"}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}
